<script>
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useTools from "@/use/Tools.js";
import {useSecurityStore} from "@/stores/Security.js";
import {useEvaluationMeasureStore} from "@/stores/Evaluation/EvaluationMeasure";
import {computed, watch, ref} from "vue";
import axios from "axios";

export default {
    props: {
        type: String,
    },
    setup(props) {
        const {
            loading,
            total,
            filter,
        } = useSharedData();

        const {transformFilterValues} = useSharedMethod();

        const {$router, $i18n} = useTools();

        const measureStore = useEvaluationMeasureStore();
        const securityStore = useSecurityStore();

        const search = ref(null);

        const headers = [
            {
                text: "...",
                sortable: false,
                align: "left",
                value: "action",
                width: "0%"
            },
            {
                text: $i18n.t("common.label"),
                align: "left",
                value: "name",
                sortable: true,
            },
            {
                text: $i18n.t("common.comment"),
                align: "left",
                value: "description",
                sortable: true,
            },
        ];

        const rootPath = computed(() => {
            if (props.type === 'EVALUATION_SKILL') {
                return 'evaluation_skill_criterion';
            } else if (props.type === 'EVALUATION_PERFORMANCE') {
                return 'evaluation_performance_criterion';
            } else if (props.type === 'EVALUATION_RECRUITMENT') {
                return 'evaluation_recruitment_criterion';
            } else if (props.type === 'EVALUATION_TRAINING_HOT') {
                return 'evaluation_training_hot_criterion';
            } else if (props.type === 'EVALUATION_TRAINING_COLD') {
                return 'evaluation_training_cold_criterion';
            }
        });

        const actions = [
            {
                title: $i18n.t("common.edit"),
                icon: 'mdi-pencil-box-multiple',
                color: 'orange',
                access: () => securityStore.authorities['UPDATE_EVALUATION_PARAM'],
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `${rootPath.value}.edit`, params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("common.delete"),
                icon: 'mdi-trash-can',
                color: 'red',
                access: () => securityStore.authorities['UPDATE_EVALUATION_PARAM'],
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `${rootPath.value}.delete`, params: {id: item.id}})
                },
            },
        ];

        function findAll() {
            loading.value = true;
            filter.value.type = props.type;
            axios.get('/evaluationMeasures', {params: transformFilterValues(filter.value)}).then((response) => {
                measureStore.setEvaluationMeasures(response.data);
                total.value = response.data.length;
            }).finally(() => {
                loading.value = false;
            });
        }

        function view(item) {
            $router.push({name: `${rootPath.value}.view`, params: {id: item.id}});
        }

        watch(() => filter.value, () => {
            findAll();
        }, {deep: true, immediate: true});

        return {
            measureStore,
            securityStore,
            headers,
            actions,
            loading,
            total,
            filter,
            rootPath,
            search,
            findAll,
            view,
        };
    },
}
</script>

<template>
    <div>
        <w-app
            :title="$t(`module.${rootPath}`)"
            :total="total"
        >
            <template v-slot:rightToolbar>
                <w-btn-reload
                    :loading="loading"
                    @press="findAll"
                />
                <w-btn-add
                    :show="securityStore.authorities['UPDATE_EVALUATION_PARAM']"
                    @press="$router.push({name: `${rootPath}.add`})"
                />
            </template>

            <template v-slot:content>
                <v-container fluid>
                    <v-row dense>
                        <v-col cols="3">
                            <w-text-field
                                v-model="search"
                                :label="$t('common.search')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
                <w-data-table
                    :headers="headers"
                    :actions="actions"
                    :items="measureStore.evaluationMeasures"
                    :loading="loading"
                    :search="search"
                    sort-by="name"
                    dense
                    @rowDbClick:row="view"
                >
                    <template v-slot:[`item.name`]="{ item }">
                        <w-inline-text bold :text="item.name" />
                        <w-btn-icon-help :text="item.notes"/>
                    </template>
                </w-data-table>
            </template>
        </w-app>

        <router-view/>
    </div>
</template>